/**
 * AB#1904 (W-1) — Stripe Checkout return: success page.
 *
 * Route: `/subscription/success?session_id=cs_test_…&toPlan=Growth`
 *
 * Five render states per Dina §B:
 *   verifying     — initial render, 0..3 s
 *   success       — current sub matches target plan → unlock-grid + CTAs
 *   processing    — webhook still pending → keep polling (every 2s for 30s)
 *   pollingTimeout — webhook still not in after 30s → amber state w/ refresh
 *   verificationError — missing session_id or hard error
 *
 * Unauth tolerance: the owner may bounce back from Stripe with a stale
 * token (the original mint-session JWT may have expired during the Stripe
 * step). If `/api/subscriptions/current` returns 401, we redirect to
 * `/form/login?returnTo=/subscription/success&session_id=…` so the
 * owner re-logs in and lands back here.
 *
 * Faris §3 Q4: synchronous cap-raise on webhook + 30s FE poll window.
 * Founder R-5 (CR-2026-06-10): `maxBranches: null` → display "Unlimited".
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService, type SubscriptionDto, type PlanTier } from '../../../services/api';
import { queryKeys } from '../../../lib/queryKeys';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import UnlockedFeatureCard from '../../../components/UnlockedFeatureCard/UnlockedFeatureCard';
import { trackEvent } from '../../../lib/analytics';
import { getSupportContact } from '../../../lib/support/supportContact';
import s from './SuccessPage.module.css';

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_MS = 30_000;
const POLL_MAX_ATTEMPTS = Math.floor(POLL_MAX_MS / POLL_INTERVAL_MS); // 15

type SuccessState =
    | 'verifying'
    | 'success'
    | 'processing'
    | 'pollingTimeout'
    | 'verificationError';

interface UnlockCard {
    iconKey: string;
    titleKey: string;
    subKey: string;
    /** When undefined the subKey is rendered verbatim (no token substitution).
     *  When a number, it replaces `{newCap}` in the subKey. The "unlimited"
     *  case uses a dedicated `*Unlimited` subKey and leaves capValue undefined,
     *  to avoid ungrammatical literal substitutions like
     *  "Up to Unlimited locations" / "حتى بلا حدود موقعاً". */
    capValue?: number;
}

/** Per Dina §4.4 — tier→cards matrix. Founder R-5: Growth caps are NULL
 *  (unlimited) for branches; FE renders a dedicated *Unlimited subKey
 *  instead of substituting the literal string "Unlimited" / "بلا حدود"
 *  into the capped phrase (which would break Arabic grammar). */
function unlockCardsForTier(tier: string | undefined): UnlockCard[] {
    const t = (tier ?? '').toLowerCase();
    if (t === 'growth' || t === '2') {
        return [
            {
                iconKey: '📍',
                titleKey: 'subscription.unlock.branchesTitle',
                // Founder R-5: Growth = unlimited branches → no token substitution.
                subKey: 'subscription.unlock.branchesSubUnlimited',
            },
            {
                iconKey: '👥',
                titleKey: 'subscription.unlock.staffTitle',
                subKey: 'subscription.unlock.staffSub',
                capValue: 7,
            },
            {
                iconKey: '🎁',
                titleKey: 'subscription.unlock.promotionsTitle',
                subKey: 'subscription.unlock.promotionsSub',
            },
            {
                iconKey: '📊',
                titleKey: 'subscription.unlock.analyticsBasicTitle',
                subKey: 'subscription.unlock.analyticsBasicSub',
            },
        ];
    }
    if (t === 'pro' || t === '3') {
        return [
            {
                iconKey: '📍',
                titleKey: 'subscription.unlock.branchesTitle',
                subKey: 'subscription.unlock.branchesSub',
                capValue: 10,
            },
            {
                iconKey: '👥',
                titleKey: 'subscription.unlock.staffTitle',
                subKey: 'subscription.unlock.staffSub',
                capValue: 20,
            },
            {
                iconKey: '⭐',
                titleKey: 'subscription.unlock.loyaltyTitle',
                subKey: 'subscription.unlock.loyaltySub',
            },
            {
                iconKey: '📈',
                titleKey: 'subscription.unlock.analyticsAdvTitle',
                subKey: 'subscription.unlock.analyticsAdvSub',
            },
            {
                iconKey: '🔐',
                titleKey: 'subscription.unlock.rbacTitle',
                subKey: 'subscription.unlock.rbacSub',
            },
        ];
    }
    // Fallback — generic
    return [
        {
            iconKey: '✨',
            titleKey: 'subscription.unlock.promotionsTitle',
            subKey: 'subscription.unlock.promotionsSub',
        },
    ];
}

/**
 * AB#1933 — BE `SubscriptionDto` exposes only `planTier` (the enum 1..4),
 * not `planId` or a string `tier` field. `targetTier` comes off the URL
 * as a string ("Growth", "2", etc.); normalize both sides to the enum
 * value before comparing.
 */
function tierStringToEnum(s: string | undefined): PlanTier | undefined {
    if (!s) return undefined;
    const k = s.trim().toLowerCase();
    if (k === 'starter' || k === '1') return 1;
    if (k === 'growth' || k === '2') return 2;
    if (k === 'pro' || k === '3') return 3;
    if (k === 'enterprise' || k === '4') return 4;
    return undefined;
}

function planMatches(sub: SubscriptionDto | null, targetTier: string | undefined): boolean {
    if (!sub) return false;
    const t = tierStringToEnum(targetTier);
    if (typeof t === 'number' && sub.planTier === t) return true;
    return false;
}

const SuccessPage: React.FC = () => {
    const { t } = useLanguage();
    const { isAuthenticated } = useAuthContext();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const sessionId = searchParams.get('session_id');
    const targetTier = searchParams.get('toTier') ?? searchParams.get('tier') ?? undefined;
    // AB#1933 — `toPlanId` is no longer authoritative (BE SubscriptionDto
    // dropped planId). Kept only as a fallback when callers want to surface
    // the numeric id in telemetry.
    const targetPlanName = searchParams.get('toPlanName') ?? undefined;

    const [pollAttempts, setPollAttempts] = useState(0);
    const [startedAt] = useState(() => Date.now());
    const [terminalState, setTerminalState] = useState<SuccessState | null>(null);

    // Unauth tolerance: bounce to login with returnTo if no token.
    useEffect(() => {
        if (!isAuthenticated) {
            const back = encodeURIComponent(
                `/subscription/success${window.location.search}`,
            );
            navigate(`/form/login?returnTo=${back}`, { replace: true });
        }
    }, [isAuthenticated, navigate]);

    // If session_id missing → immediate error fork.
    useEffect(() => {
        if (!sessionId && isAuthenticated) {
            setTerminalState('verificationError');
            trackEvent('subscription.upgradeVerificationFailed', {
                sessionId: '',
                errorCode: 'MissingSessionId',
            });
        }
    }, [sessionId, isAuthenticated]);

    // Poll /current every 2 s, stop on match or after 30 s.
    // TanStack v5: refetchInterval receives the Query object — read .state.data.
    const query = useQuery<SubscriptionDto | null>({
        queryKey: queryKeys.subscription.current(),
        queryFn: () => apiService.getCurrentSubscription(),
        enabled: isAuthenticated && !!sessionId && !terminalState,
        refetchInterval: q => {
            const d = q.state.data ?? null;
            if (planMatches(d, targetTier)) return false;
            return POLL_INTERVAL_MS;
        },
        refetchOnWindowFocus: true,
        staleTime: 0,
    });

    // Watch results to drive the state machine.
    useEffect(() => {
        if (terminalState) return;
        if (!query.data && !query.isError) return;

        if (planMatches(query.data ?? null, targetTier)) {
            setTerminalState('success');
            // Arm the dashboard banner one-shot.
            try {
                // AB#1933 — banner one-shot keyed on planTier (enum 1..4)
                // since SubscriptionDto no longer carries planId.
                sessionStorage.setItem(
                    'devanza:subscriptionUpgraded',
                    JSON.stringify({
                        planTier: query.data!.planTier,
                        planName: query.data!.planName,
                        timestamp: Date.now(),
                    }),
                );
            } catch { /* noop */ }

            // Invalidate any plan-cap-aware queries so the next mount of
            // /my-business/branches sees the raised cap immediately
            // (Faris §3 Q4 path 1 — optimistic re-fetch on success page).
            queryClient.invalidateQueries({ queryKey: queryKeys.subscription.current() });
            queryClient.invalidateQueries({ queryKey: queryKeys.branches.all });

            trackEvent('subscription.upgradeCompleted', {
                toPlan: query.data!.planName,
                pollAttempts,
            });
            return;
        }

        // Increment attempt counter on each refetch tick (data updates).
        if (query.dataUpdatedAt > startedAt) {
            setPollAttempts(a => a + 1);
        }

        // Past 30s with no match → pollingTimeout
        if (Date.now() - startedAt > POLL_MAX_MS) {
            setTerminalState('pollingTimeout');
            trackEvent('subscription.upgradePollingTimeout', {
                toPlan: targetPlanName ?? targetTier ?? '',
                sessionId: sessionId ?? '',
            });
        }
    }, [
        query.data,
        query.isError,
        query.dataUpdatedAt,
        startedAt,
        targetTier,
        terminalState,
        sessionId,
        targetPlanName,
        queryClient,
        pollAttempts,
    ]);

    // Once we've been polling > 3s without success, render processing UI.
    const inProcessing = useMemo(() => {
        if (terminalState) return false;
        return Date.now() - startedAt > 3_000;
    }, [terminalState, startedAt]);

    // Fire processing event once on entry.
    const [processingFired, setProcessingFired] = useState(false);
    useEffect(() => {
        if (inProcessing && !processingFired) {
            trackEvent('subscription.upgradeProcessing', {
                toPlan: targetPlanName ?? targetTier ?? '',
            });
            setProcessingFired(true);
        }
    }, [inProcessing, processingFired, targetPlanName, targetTier]);

    const renderState: SuccessState = terminalState ?? (inProcessing ? 'processing' : 'verifying');

    // ---- Render forks ---------------------------------------------------

    if (!isAuthenticated) return null; // mid-redirect

    if (renderState === 'verifying' || renderState === 'processing') {
        const isProc = renderState === 'processing';
        return (
            <div className={s.page}>
                <div className={s.card} role="status" aria-live="polite">
                    <div className={s.spinner} aria-hidden="true" />
                    <h2 className={s.title}>
                        {isProc
                            ? t('subscription.success.processingTitle')
                            : t('subscription.success.verifyingTitle')}
                    </h2>
                    <p className={s.sub}>
                        {isProc
                            ? t('subscription.success.processingSub')
                            : t('subscription.success.verifyingBody')}
                    </p>
                    {isProc && pollAttempts > 1 && (
                        <p className={s.attempt}>
                            {t('subscription.success.pollAttempt').replace(
                                '{n}',
                                String(Math.min(pollAttempts, POLL_MAX_ATTEMPTS)),
                            )}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (renderState === 'pollingTimeout') {
        return (
            <div className={s.page}>
                <div className={s.card} role="status">
                    <div className={`${s.iconCircle} ${s.iconAmber}`} aria-hidden="true">⚠</div>
                    <h2 className={s.title}>{t('subscription.success.timeoutTitle')}</h2>
                    <p className={s.sub}>{t('subscription.success.timeoutBody')}</p>
                    <div className={s.actions}>
                        <button
                            type="button"
                            className={s.btnPrimary}
                            onClick={() => {
                                setTerminalState(null);
                                setPollAttempts(0);
                                query.refetch();
                            }}
                        >
                            {t('subscription.success.timeoutRefresh')}
                        </button>
                        <button
                            type="button"
                            className={s.btnGhost}
                            onClick={() => navigate('/dashboard')}
                        >
                            {t('subscription.success.timeoutContinue')}
                        </button>
                    </div>
                    {sessionId && (
                        <p className={s.footer}>
                            {t('subscription.success.timeoutFooter')
                                .replace('{oldPlanName}', query.data?.planName ?? '—')
                                .replace('{session_id}', sessionId)}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (renderState === 'verificationError') {
        const safeSessionId = sessionId ?? '';
        const supportEmail = getSupportContact().email;
        return (
            <div className={s.page}>
                <div className={s.card} role="alert">
                    <div className={`${s.iconCircle} ${s.iconRed}`} aria-hidden="true">✗</div>
                    <h2 className={s.title}>{t('subscription.success.errorTitle')}</h2>
                    <p className={s.sub}>{t('subscription.success.errorBody')}</p>
                    {safeSessionId && (
                        <p className={s.sessionPill} title={safeSessionId}>
                            <code>{safeSessionId}</code>
                        </p>
                    )}
                    {/* AB#2133 — contact only via the founder-configured address;
                        never the uncontrolled devanza.com domain. */}
                    {!supportEmail && (
                        <p className={s.footer}>{t('support.contact.notConfigured')}</p>
                    )}
                    <div className={s.actions}>
                        {supportEmail && (
                            <a
                                className={s.btnPrimary}
                                href={`mailto:${supportEmail}?subject=${encodeURIComponent(
                                    `Upgrade verification failed — session ${safeSessionId}`,
                                )}`}
                            >
                                {t('subscription.success.errorContact')}
                            </a>
                        )}
                        <button
                            type="button"
                            className={s.btnGhost}
                            onClick={() => navigate('/subscription')}
                        >
                            {t('subscription.success.errorBack')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // success
    const planName = query.data?.planName ?? targetPlanName ?? '';
    // AB#1933 — pass the planTier enum as a string so the existing
    // ('2'|'3') matchers in unlockCardsForTier still hit.
    const cards = unlockCardsForTier(
        query.data?.planTier != null ? String(query.data.planTier) : targetTier,
    );

    return (
        <div className={s.page}>
            <div className={s.card}>
                <div className={`${s.iconCircle} ${s.iconGreen}`} aria-hidden="true">✓</div>
                <h1 className={s.title}>
                    {t('subscription.success.title').replace('{planName}', planName)}
                </h1>
                <p className={s.sub}>{t('subscription.success.sub')}</p>

                <div className={s.unlockGrid}>
                    {cards.map((c, i) => (
                        <UnlockedFeatureCard
                            key={`${c.titleKey}-${i}`}
                            icon={c.iconKey}
                            title={t(c.titleKey)}
                            description={
                                c.capValue !== undefined
                                    ? t(c.subKey).replace('{newCap}', String(c.capValue))
                                    : t(c.subKey)
                            }
                        />
                    ))}
                </div>

                <div className={s.actions}>
                    <button
                        type="button"
                        className={s.btnPrimary}
                        onClick={() => navigate('/my-business/branches')}
                    >
                        {t('subscription.success.ctaMyBusiness')}
                    </button>
                    <button
                        type="button"
                        className={s.btnGhost}
                        onClick={() => navigate('/dashboard')}
                    >
                        {t('subscription.success.ctaDashboard')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SuccessPage;
